import uuid from 'uuid';
import _ from 'lodash';
import { MagicSearchPluginAsync, MagicSearchPluginName, ResultItem } from './types';
import { UseQueryOptions, QueryClient } from '@tanstack/react-query-v4';
import {
  SearchResultsListHeader,
  SearchResultsListHeaderProps,
  SearchResultsListItemLoading,
  SearchResultsListItemLink,
} from '../components';
import Fuse from 'fuse.js';
import memoizeOne from 'memoize-one';

const createFuseSearchIndex = memoizeOne(
  (indexItems: any[]) =>
    new Fuse<any>(indexItems, {
      ignoreLocation: true,
      threshold: 0.2,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 3,
      shouldSort: true,
      keys: [
        // If you want to show results that are in another field, you should add it here
        { name: 'name', weight: 0.2 },
        { name: 'description', weight: 0.2 },
        { name: 'id', weight: 0.2 },
      ],
      fieldNormWeight: 1.2,
    }),
);

const reactQueryPlugin = ({
  basePath,
  headerProps,
  hint,
  queryClient,
  queryFactoryFunction,
}: {
  basePath: string;
  hint: string;
  headerProps: SearchResultsListHeaderProps;
  queryClient: QueryClient;
  queryFactoryFunction: ({ searchText }: { searchText: string }) => UseQueryOptions;
}): MagicSearchPluginAsync => {
  const searchResultsListItemLoadingIds = [uuid.v4(), uuid.v4()];
  const searchResultsListItemErrorId = uuid.v4();
  const isMongoId = (searchText: string) => /^[0-9a-fA-F]{24}$/.test(searchText);
  return {
    name: MagicSearchPluginName.ReactQueryPlugin,
    hint: ({ searchScopes }) => (!searchScopes.some(scope => scope.id === 'project') ? hint : null),
    onSearching: () => {
      const results: ResultItem[] = [];

      results.push(
        ...searchResultsListItemLoadingIds.map(id => ({
          id,
          Component: SearchResultsListItemLoading,
          props: {
            icon: ['far', 'folder'],
          },
        })),
      );

      return results;
    },
    onSearch: async ({ searchText }) => {
      const results: ResultItem[] = [];
      try {
        if (!searchText) {
          return [];
        } else {
          const { queryFn, queryKey } = queryFactoryFunction({ searchText });
          if (queryFn && queryKey) {
            if (queryKey[0] === 'projects' && queryKey[1] === 'search' && isMongoId(searchText)) {
              return [];
            }

            const data = await queryClient.ensureQueryData({ queryFn, queryKey });
            if (_.isArray(data)) {
              if (!data.length) {
                return [];
              }
              const fuseSearchIndex = createFuseSearchIndex(
                // in case you use others fields in the query, you should add them here to be available in the item of the fuseSearchIndexResults
                data.map(
                  ({
                    id,
                    name,
                    redirectLookup,
                    redirectLookupSearch,
                    description,
                    isActive,
                    type,
                  }) => ({
                    id,
                    name,
                    description,
                    redirectLookup,
                    redirectLookupSearch,
                    isActive,
                    type,
                  }),
                ),
              );
              let fuseSearchIndexResults = fuseSearchIndex.search(searchText);
              fuseSearchIndexResults = fuseSearchIndexResults.slice(0, 10);
              fuseSearchIndexResults = fuseSearchIndexResults.filter(result => {
                // lower scores are more relevant, and if there's no score, we just assume it's relevant
                // as the weight of the fields is 0.2 (lower than others), we set a higher filter to show more results.
                return (result.score ?? 1) < 0.6;
              });
              if (queryKey[0] === 'lookup') {
                results.push(
                  ...fuseSearchIndexResults.map(
                    ({
                      item: { id, name, redirectLookup, redirectLookupSearch, type },
                      score,
                    }) => ({
                      id,
                      Component: SearchResultsListItemLink,
                      props: {
                        title: name,
                        description: id,
                        icon: ['far', 'folder'],
                        to: {
                          pathname: redirectLookup,
                          search: redirectLookupSearch ?? '',
                        },
                        type: headerProps.children,
                      },
                      score: type === 'Corp' ? 1 : score, // this way if the result is a corp lookup, it will be the last result
                    }),
                  ),
                );
              } else {
                results.push(
                  ...fuseSearchIndexResults.map(
                    ({ item: { id, name, description, isActive }, score }) => ({
                      id,
                      Component: SearchResultsListItemLink,
                      props: {
                        title: name,
                        description: description || id,
                        icon: ['far', 'folder'],
                        variant: isActive ? 'success' : 'warning',
                        to: {
                          pathname: `${basePath}/${id}`,
                        },
                        type: headerProps.children,
                      },
                      score,
                    }),
                  ),
                );
              }
            }
          }
        }
      } catch (error) {
        results.push({
          id: searchResultsListItemErrorId,
          Component: SearchResultsListHeader,
          props: {
            children: `${headerProps.children} ERROR`,
          },
        });
        return [];
      }

      return results;
    },
  };
};

export { reactQueryPlugin };
